import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { useAppState } from "../context/AppStateContext";
import { fetchStoreStocks, type StoreStockRow } from "../api/mrrInventoryApi";

export function StoreStocksPage() {
  const { storeYards } = useAppState();
  const sortedYards = useMemo(() => [...storeYards].sort((a, b) => a.sortOrder - b.sortOrder), [storeYards]);
  const [filterYardId, setFilterYardId] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [rows, setRows] = useState<StoreStockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filteredRows = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const name = (r.resourceItemLabel ?? "").toLowerCase();
      const idStr = String(r.catalogStandardItemId);
      return name.includes(q) || idStr.includes(q);
    });
  }, [rows, itemSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchStoreStocks(filterYardId.trim() || undefined);
      setRows(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filterYardId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Inventory", to: "/inventory/inward" },
          { label: "Store stocks" },
        ]}
        title="Stock by store / yard"
      />

      <div className="card card--gov card--elevated">
        <p className="section-lead section-lead--tight text-muted" style={{ marginTop: 0 }}>
          Total inward and QC rejects come from goods receipts. Total outward sums the store issue ledger (zero until outward
          is recorded). Current stock = inward − reject − outward (not below zero). Names load from the MRR database when
          configured.{" "}
          <Link to="/inventory/goods-receipts">Goods receipts</Link>
          {" · "}
          <Link to="/inventory/inward">Inward</Link>
          {" · "}
          <Link to="/inventory/store-audit">Store audit</Link>
        </p>

        <div className="goods-receipts-filters" style={{ marginBottom: "1rem" }}>
          <div className="goods-receipts-filters__grid">
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Filter by store / yard</span>
              <select
                className="form-control"
                value={filterYardId}
                onChange={(e) => setFilterYardId(e.target.value)}
              >
                <option value="">All stores / yards</option>
                {sortedYards.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="goods-receipts-filters__field">
              <span className="goods-receipts-filters__label">Search item</span>
              <input
                id="store-stocks-item-search"
                type="search"
                className="form-control"
                placeholder="Name or item id"
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                autoComplete="off"
              />
            </label>
          </div>
        </div>

        {loading ? (
          <p className="text-muted">Loading…</p>
        ) : error ? (
          <p className="text-muted" style={{ color: "var(--dash-danger, #b91c1c)" }}>
            {error}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-muted">No stock movements recorded yet (save a goods receipt from Inward first).</p>
        ) : filteredRows.length === 0 ? (
          <p className="text-muted">No items match your search. Try a different term or clear the search box.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Store / yard</th>
                  <th>Item</th>
                  <th>Unit</th>
                  <th>Total inward</th>
                  <th>Total outward</th>
                  <th>Current stock</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <tr key={`${r.storeYardId}-${r.catalogStandardItemId}-${r.resourceItemLabel}`}>
                    <td>{r.storeYardName || r.storeYardId}</td>
                    <td>{r.resourceItemLabel || "—"}</td>
                    <td>{r.resourceItemUnit || "—"}</td>
                    <td>{r.totalInward}</td>
                    <td>{r.totalOutward > 0 ? r.totalOutward : "—"}</td>
                    <td>
                      <strong>{r.currentStock}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
